<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-02-26 (0.1.0) (jhoward)
 *   - Add PeppermillNotifications and PeppermillOverlay components.
 *   - Update layout structure and CSS for new UI elements and responsive design.
 *   - Wire up notifications ref to appStore.
 *
 * 2026-02-27 (0.2.0) (jhoward)
 *   - Add logout method using custom fetch and update app state on logout.
 *   - Add links for Enroll and 2FA enrollment in footer.
 *   - Refactor layout for new footer and user state.
 *
 * 2026-02-28 (0.3.0) (jhoward)
 *   - Add router-links for Home and Account Info when user is authenticated.
 *
 * 2026-03-11 (0.4.0) (jhoward)
 *   - Refactor navigation to use AppHeader and AppNavigation components; remove old footer links.
 *   - Add computed navLinks and containerClasses for dynamic navigation and styling.
 *   - Add reauthentication dialog with password input and logic for reauthentication flow.
 *   - Replace direct fetch helpers with peppermillCustomFetchProxy.
 *   - Update template to use new navigation and header components.
 *   - Update transition and animation CSS; add media queries for reduced motion.
 *   - BREAKING: Removes old logout logic and footer navigation, requiring migration to new navigation components.
 *
 * 2026-03-13 (0.5.0) (jhoward)
 *   - Add OfferManagementDialog component for offer actions.
 *   - Add fade transition for dialogs.
 *
 * 2026-03-13 (0.5.1) (jhoward)
 *   - Reset reauthenticatePassword on successful reauth.
 *   - Wrap reauthentication dialog in a transition for better UX.
 *
 * 2026-03-17 (0.6.0) (jhoward)
 *   - Add admin and user management links to navigation for users with is_admin property.
 *
 * 2026-03-24 (0.7.0) (jhoward)
 *   - Add 'Maintenance Mode' admin nav link and route.
 *   - Add logoutUser() for reauthentication dialog deny action.
 *   - Show 'Logout' button in reauthentication dialog.
 *
 * 2026-03-30 (0.8.0) (jhoward)
 *   - Add navigation links for Access Log and User Log for admin users.
 *   - Add CookieDisclosure component and related logic to footer.
 *
 * 2026-04-09 (0.9.0) (jhoward)
 *   - Add navigation links for 'User Lookup', 'Retrieve My Username', and 'Reset My Password'.
 *   - Adjust nav container padding.
 *
 * 2026-08-24 (0.9.1) (jhoward)
 *   - Comment out .nav--container ~ .body--container CSS rule; no user-facing impact.
 *
 * 2026-08-27 (0.9.2) (jhoward)
 *   - Cast appStore.state.user to User when checking is_admin, since it's already gated on
 *     twoFactorAuthRequired being false (the full user shape is only sent post-2FA).
 *
 * 2026-09-18 (0.10.0) (jhoward)
 *   - Rename the 'Refresh Offers' admin link to 'Update Login Data' and point it at /login-data/update, which
 *     refetches the account details and tournament results along with the offers.
 *
 * 2026-10-09 (0.11.0) (jhoward)
 *   - Add an 'FAQ' link for everyone, signed in or not, and an 'FAQ' admin link to /admin/faq.
 *   - Add a 'Support Assistant' admin link to /admin/assistant.
 *   - Mount GuestHelpWidget, the guest FAQ bot, on every page except /admin.
 *   - Give .body--container the framework's M3 body-large type and on-surface colour. It named robotolight and
 *     robotoregular, which nothing loads, so every page's text fell back to the generic sans-serif; the framework's
 *     own container fixed the same bug in 3.20. Section headings take headline-small.
 *   - Make .body--container overflow-x: clip rather than a second scroller (framework 3.35, container 1.5.1).
 *   - Give the Home link the 'home' icon. It shared 'sell' with Offers, so the two destinations looked the same.
 *   - Fix the headline-small rule above zeroing the space between a content block and the heading after it (account info,
 *     and any view that stacks blocks): it tied the framework's 64px rule on specificity and won on order. It is now
 *     wrapped in :where().
 *   - Add the 'User Two-Factor Reset' admin link.
 *
 * 2026-10-10 (0.12.0) (jhoward)
 *   - Keep Home, ViewOffers, ViewAccountInfo and ViewWinLoss alive, so a revisit does not remount them and refetch. The
 *     include list is empty while nobody is signed in: <KeepAlive> prunes what no longer matches, so logging out (or a 401)
 *     drops every cached page and the next patron cannot be shown the last one's data.
 *   - Drop the empty :exclude.
 *   - Add the 'Sign-in & Security' link under Account.
 *
 * 2026-10-10 (0.13.0) (jhoward)
 *   - Show a "Protected" badge in the navigation for a guest with a passkey or authenticator app.
 *
 * 2026-10-10 (0.14.0) (jhoward)
 *   - Mount PasswordSecurityBanner beside the maintenance banner, outside <main> for the same KeepAlive reason.
 *
 * 2026-10-10 (0.15.0) (jhoward)
 *   - Add the 'Security Audit' and 'Security Settings' admin links.
 *
 * 2026-10-09 (0.16.0) (jhoward)
 *   - Add a 'Chatbots' admin link to /admin/chatbot, the chatbot on/off switches.
 *
 * 2026-10-28 (0.17.0) (jhoward)
 *   - When the property is shut off (appStore.propertyShutoff), show a maintenance notice in place of the navigation and
 *     the router view, so no route can render. A browser with the administrator's access link gets the site and a banner.
 */

import PeppermillContentBlock from '@framework/components/layout/PeppermillContentBlock.vue';
import PeppermillNotifications from '@framework/components/PeppermillNotifications.vue';
import type { PeppermillNotificationsComponent } from '@framework/components/PeppermillNotifications.vue';
import PeppermillOverlay from '@framework/components/PeppermillOverlay.vue';
import type { PeppermillUserNavLink } from '@framework/defs/user';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import AppHeader from '@/components/AppHeader.vue';
import type { NavBadge } from '@/components/AppNavigation.vue';
import AppNavigation from '@/components/AppNavigation.vue';
import CookieDisclosure from '@/components/CookieDisclosure.vue';
import GuestHelpWidget from '@/components/GuestHelpWidget.vue';
import MaintenanceWindowBanner from '@/components/MaintenanceWindowBanner.vue';
import OfferManagementDialog from '@/components/offers/OfferManagementDialog.vue';
import ReauthenticateDialog from '@/components/ReauthenticateDialog.vue';
import type { User } from '@/defs/user';
import getRouteWithFixedUrl from '@/helpers/getRouteWithFixedUrl';
import router from '@/router';
import useAppStore from '@/store';
import { username as usernameRoute } from '@rss/routes/login/help';
import { request as passwordRequest } from '@rss/routes/password';

const route = useRoute();
const appStore = useAppStore();
const notifications = useTemplateRef<PeppermillNotificationsComponent | null>('notifications');
const isCookieDisclosureEnabled = ref<boolean>(false);

/*
 * The views kept alive, by component name - the filename, not the route name. The signed-in pages that read account data
 * and can be refreshed on a timer (useRevalidateOnActivate). Not kept: the login, two-factor, verification and recovery
 * pages (one-time codes and tokens, and Logout has to run every time), the admin tools (lookups show other patrons'
 * data, and a stale value is worse than a refetch), and the per-id pages, which would add an instance for every id.
 */
const cachedViews = ['Home', 'ViewOffers', 'ViewAccountInfo', 'ViewWinLoss'];

//The dialog owns the method choice and the verification; this only has to release whatever was waiting on it.
async function handleReauthenticationSuccess() {
  appStore.state.reauthenticationRequired = false;
  if (appStore.state.reauthenticationCallback) {
    await appStore.state.reauthenticationCallback(true);
    appStore.state.reauthenticationCallback = null;
  }
}

function logoutUser() {
  appStore.state.reauthenticationRequired = false;
  //Released with false before it is dropped. The call that opened this dialog is still awaiting an answer, and
  //leaving without giving it one leaves that promise pending for the life of the page.
  appStore.state.reauthenticationCallback?.(false);
  appStore.state.reauthenticationCallback = null;
  router.push({ name: 'logout' });
}

const navLinks = computed(() => {
  const output: PeppermillUserNavLink[] = [];
  if (appStore.state.user) {
    if (!appStore.state.twoFactorAuthRequired) {
      output.push({ title: 'Home', path: '/', icon: 'home', isSub: false });
      output.push({ title: 'Offers', path: '/offers', icon: 'sell', isSub: false });
      output.push({ title: 'Account', path: '/info', icon: 'user_attributes', isSub: false });
      output.push({ title: 'Sign-in & Security', path: '/account/security', icon: 'verified_user', isSub: true });
      output.push({ title: 'Win/Loss', path: '/win-loss', icon: 'print', isSub: false });

      if ((appStore.state.user as User | null)?.is_admin) {
        output.push({ title: 'Admin', path: '/admin', icon: 'supervisor_account', isSub: false });
        output.push({ title: 'Support Assistant', path: '/admin/assistant', icon: 'support_agent', isSub: true });
        output.push({ title: 'User Password Help', path: '/admin/user/password', icon: 'password', isSub: true });
        output.push({ title: 'User Email/Phone Help', path: '/admin/user/email-or-phone', icon: 'email', isSub: true });
        output.push({ title: 'User Username Help', path: '/admin/user/username', icon: 'account_circle', isSub: true });
        output.push({
          title: 'User Two-Factor Reset',
          path: '/admin/user/two-factor',
          icon: 'lock_reset',
          isSub: true,
        });
        output.push({ title: 'Security Audit', path: '/admin/security-audit', icon: 'security', isSub: true });
        output.push({ title: 'Security Settings', path: '/admin/settings/security', icon: 'admin_panel_settings', isSub: true });
        output.push({ title: 'Maintenance Mode', path: '/admin/maintenance-mode', icon: 'construction', isSub: true });
        output.push({ title: 'Access Log', path: '/access-log', icon: 'library_books', isSub: true });
        output.push({ title: 'User Log', path: '/user-log', icon: 'user_attributes', isSub: true });
        output.push({ title: 'User Lookup', path: '/user-lookup', icon: 'data_loss_prevention', isSub: true });
        output.push({ title: 'Update Login Data', path: '/login-data/update', icon: 'refresh', isSub: true });
        output.push({ title: 'FAQ', path: '/admin/faq', icon: 'quiz', isSub: true });
        output.push({ title: 'Chatbots', path: '/admin/chatbot', icon: 'smart_toy', isSub: true });
      }
    }

    output.push({ title: 'FAQ', path: '/faq', icon: 'help', isSub: false });
    output.push({ title: 'Logout', path: '/logout', icon: 'logout', isSub: false });
  } else {
    output.push({ title: 'Login', path: '/login', icon: 'login', isSub: false });
    output.push({
      title: 'Retrieve My Username',
      path: getRouteWithFixedUrl(usernameRoute()).url,
      icon: 'account_circle',
      isSub: true,
    });
    output.push({
      title: 'Reset My Password',
      path: getRouteWithFixedUrl(passwordRequest()).url,
      icon: 'password',
      isSub: true,
    });
    output.push({ title: 'Enroll', path: '/enroll', icon: 'how_to_reg', isSub: false });
    output.push({ title: 'FAQ', path: '/faq', icon: 'help', isSub: false });
  }

  return output;
});

//The reward made visible: a guest past two-factor who holds a passkey or an authenticator app.
const protectedBadge = computed<NavBadge | null>(() => {
  const user = appStore.state.user as User | null;

  return user && !appStore.state.twoFactorAuthRequired && true === user.has_strong_two_factor
    ? { label: 'Protected', icon: 'verified_user' }
    : null;
});

/**
 * This property's site is shut off and this browser has not opened the administrator's access link. The server answers
 * 503 to everything but this page in that case (App\Http\Middleware\PropertyShutoffMiddleware); this is the page.
 */
const isShutOff = computed<boolean>(() => appStore.propertyShutoff && !appStore.propertyShutoffBypass);
const shutoffMessage = "Maintenance is underway. We'll be back soon.";

onMounted(() => {
  appStore.notificationsComponent = notifications.value;
});
</script>

<template>
  <!--
    Both the maintenance banner and the cookie disclosure are bottom-fixed. They cannot collide today, because
    isCookieDisclosureEnabled is hardcoded false - this is here so that the day it is turned on is not also the day
    they land on top of each other.
  -->
  <div id="app-container" :style="{ '--maintenance-banner-bottom': isCookieDisclosureEnabled ? '9em' : '1em' }">
    <app-header></app-header>

    <!--
      A shut-off property shows only this: no navigation and no router-view, so no page (and none of its fetches or
      passkey prompts) can run behind it. A browser holding the administrator's access link gets the site and a notice.
    -->
    <main v-if="isShutOff" class="body--container app--shutoff" role="alert">
      <peppermill-content-block>
        <h2>Maintenance</h2>
        <p>{{ shutoffMessage }}</p>
      </peppermill-content-block>
    </main>
    <template v-else>
      <p v-if="appStore.propertyShutoff" class="app--shutoff-banner" role="status">
        This property is shut off to guests. You are seeing it through an administrator access link.
      </p>
      <app-navigation :links="navLinks" :badge="protectedBadge"></app-navigation>

      <main class="body--container">
        <router-view v-slot="{ Component }">
          <Transition name="slide" mode="out-in">
            <KeepAlive :max="10" :include="appStore.state.user ? cachedViews : []">
              <component :is="Component" :key="route.fullPath"></component>
            </KeepAlive>
          </Transition>
        </router-view>
      </main>
    </template>

    <!--
      Rendered unconditionally: the component owns the polling lifecycle and draws nothing when there is no window
      to warn about. It sits here, inside #app-container but outside <main>, so it survives every route change -
      putting it in <main> would hand it to <KeepAlive>, which would suspend and then duplicate the poll.
    -->
    <maintenance-window-banner></maintenance-window-banner>

    <!-- Same placement and reason as the banner: outside <main>, so KeepAlive never duplicates it. Not on /admin
         screens, which have the staff assistant instead. -->
    <guest-help-widget v-if="!isShutOff && !route.path.startsWith('/admin')"></guest-help-widget>

    <footer v-if="isCookieDisclosureEnabled">
      <cookie-disclosure></cookie-disclosure>
    </footer>

    <peppermill-overlay
      :is-active="appStore.isOverlayActive"
      :is-loading-spinner-active="appStore.isLoadingSpinnerActive"
      :text="appStore.overlayText"
    ></peppermill-overlay>

    <peppermill-notifications ref="notifications"></peppermill-notifications>

    <transition name="fade">
      <reauthenticate-dialog
        v-if="appStore.state.reauthenticationRequired"
        @success="handleReauthenticationSuccess"
        @logout="logoutUser"
      ></reauthenticate-dialog>
    </transition>

    <transition name="fade">
      <offer-management-dialog
        v-if="appStore.offerManagementDialog.offer"
        is-active
        :offer="appStore.offerManagementDialog.offer"
        @success="appStore.offerManagementDialog.offer = null"
        @cancel="appStore.offerManagementDialog.offer = null"
      ></offer-management-dialog>
    </transition>
  </div>
</template>

<style scoped>
#app--container {
  margin-bottom: 2.5em;
}

/*.nav--container {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  background: rgb(var(--background-color));
  box-shadow: 2px 2px 8px 0 rgba(0, 0, 0, 0.35);
  padding: 0.25em;
}

.nav--container a {
  display: grid;
  grid-template-rows: auto auto;
  justify-items: center;
  text-decoration: none;
  color: blue;
  transition: color 150ms cubic-bezier(0.4, 0, 0.2, 1), scale 150ms cubic-bezier(0.4, 0, 0.2, 1), text-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: bottom center;
}

.nav--container a.router-link-active {
  scale: 1.5;
  color: rgb(var(--primary-color));
  text-shadow: 0 0 2em rgba(0,0,0,1);
}*/

.slide-enter-active,
.slide_reverse-enter-active,
.slide-leave-active,
.slide_reverse-leave-active {
  transition:
    opacity 350ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 350ms cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-enter-from {
  opacity: 0;
  transform: translateY(-15vh);
}

.slide_reverse-leave-to {
  opacity: 0;
  transform: translateY(15vh);
}

.slide-leave-to {
  opacity: 0;
  transform: translateY(15vh);
}

.slide_reverse-enter-from {
  opacity: 0;
  transform: translateY(15vh);
}

.slide-leave-from,
.slide_reverse-leave-from,
.slide-enter-to,
.slide_reverse-enter-to {
  opacity: 1;
  transform: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .slide-enter-active,
  .slide_reverse-enter-active,
  .slide-leave-active,
  .slide_reverse-leave-active {
    transition: none;
  }

  .slide-enter-from {
    opacity: 0;
    transform: none;
  }

  .slide_reverse-leave-to {
    opacity: 0;
    transform: none;
  }

  .slide-leave-to {
    opacity: 0;
    transform: none;
  }

  .slide_reverse-enter-from {
    opacity: 0;
    transform: none;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}

.app--shutoff-banner {
  margin: 0;
  padding: 0.5em 1em;
  text-align: center;
  background: var(--md-sys-color-tertiary-container);
  color: var(--md-sys-color-on-tertiary-container);
}

.body--container {
  /*display: grid;
  align-items: center;*/
  background-color: rgb(var(--background-color));
  color: var(--md-sys-color-on-surface);
  font-family: var(--md-sys-typescale-body-large-font);
  font-size: var(--md-sys-typescale-body-large-size);
  font-weight: var(--md-sys-typescale-body-large-weight);
  line-height: var(--md-sys-typescale-body-large-line-height);
  letter-spacing: var(--md-sys-typescale-body-large-tracking);
  text-rendering: optimizeLegibility;
  /* The document is the scroller. A second one here shows a second scrollbar when anything overflows. */
  overflow-x: clip;
  min-height: 100vh;
  padding: 4rem 1rem;
  box-sizing: border-box;
}

/*.nav--container ~ .body--container {
  padding-top: calc(200px + 2rem);
}*/

/*layout--actions overrides*/
.layout--content_block-has_actions {
  padding-bottom: 0;
}

.layout--content_block .layout--actions {
  margin-left: -24px;
  margin-right: -24px;
}

.layout--content_block .table--lazy_load .layout--actions {
  margin-left: initial;
  margin-right: initial;
}

html .layout--content_block .layout--actions {
  padding-left: 0.5em;
  padding-right: 0.5em;
  margin-left: 0;
  margin-right: 0;
}

.layout--viewport {
  max-width: 1160px;
  margin: 0 auto;
}

/* Deep, so a view's own headings take the M3 headline-small style, as the framework's container gives them.
   :where() keeps the selector's specificity below the framework's `.layout--content_block + h2` rules, which put 64px
   above a heading that follows a block; at equal specificity this rule came later and its margin: 0 won. */
:where(.body--container) :deep(h2) {
  font-family: var(--md-sys-typescale-headline-small-font);
  font-size: var(--md-sys-typescale-headline-small-size);
  font-weight: var(--md-sys-typescale-headline-small-weight);
  line-height: var(--md-sys-typescale-headline-small-line-height);
  margin: 0;
}

.layout--content_block .layout--scroller {
  overflow: hidden;
  overflow-x: auto;
  overflow-y: hidden;
}

.form--container .layout--content_block > * {
  grid-column: span 3;
}

.layout--content_block.layout--content_block-big_link_container > * {
  grid-column: span 2;
}

.layout--content_block.layout--content_block-big_link_container > h2 {
  grid-column: 1 / -1;
}

.form--container .layout--content_block > .grid--col-1,
.layout--content_block.layout--content_block-big_link_container > .grid--col-1 {
  grid-column: span 1;
}

.form--container .layout--content_block > .grid--col-2,
.layout--content_block.layout--content_block-big_link_container > .grid--col-2 {
  grid-column: span 2;
}

.form--container .layout--content_block > .grid--col-3,
.layout--content_block.layout--content_block-big_link_container > .grid--col-3 {
  grid-column: span 3;
}

.form--container .layout--content_block > .grid--col-4,
.layout--content_block.layout--content_block-big_link_container > .grid--col-4 {
  grid-column: span 4;
}

.form--container .layout--content_block > .grid--col-5,
.layout--content_block.layout--content_block-big_link_container > .grid--col-5 {
  grid-column: span 5;
}

.form--container .layout--content_block > .grid--col-6,
.layout--content_block.layout--content_block-big_link_container > .grid--col-6 {
  grid-column: span 6;
}

.form--container .layout--content_block > .grid--col-6,
.layout--content_block.layout--content_block-big_link_container > .grid--col-6 {
  grid-column: span 6;
}

.form--container .layout--content_block > .layout--content-wide {
  grid-column: 1 / 7;
}

.form--container .layout--content_block-grid_small > * {
  grid-column: span 2;
}

.form--container .layout--content_block > h1,
.form--container .layout--content_block > h2,
.form--container .layout--content_block > h3,
.form--container .layout--content_block > h4,
.form--container .layout--content_block > h5,
.form--container .layout--content_block > h6,
.form--container .layout--content_block > p,
.form--container .layout--content_block > .layout--scroller,
.form--container .layout--content_block > .table--lazy_load,
.form--container .layout--content_block > .table--content,
.form--container .layout--content_block > table,
.form--container .layout--content_block > hr {
  grid-column: 1 / 7;
}

.form--container .layout--content_block > hr {
  width: 100%;
}

.form--container .layout--content_block > button,
.form--container
  .layout--content_block
  > label:nth-last-of-type(2):first-of-type
  + label
  + button:last-of-type:first-of-type {
  grid-column: 2 / 6;
}

.form--container .layout--content_block > label:nth-of-type(2n + 1) + button {
  grid-column: span 3;
}

.form--container .layout--content_block > label:nth-of-type(2n + 1) + button:nth-last-of-type(2):first-of-type {
  grid-column: span 2;
}

.form--container
  .layout--content_block
  > label:nth-of-type(2n + 1)
  + button:nth-last-of-type(2):first-of-type
  + button {
  grid-column: span 1;
}

.form--container .layout--content_block > label:nth-of-type(2n) + button:nth-last-of-type(2):first-of-type {
  grid-column: 2 / 4;
}

.form--container .layout--content_block > label:nth-of-type(2n) + button:nth-last-of-type(2):first-of-type + button {
  grid-column: 4 / 6;
}

.form--container .form--hidden {
  display: none;
}

@media (max-width: 800px) {
  .form--container .layout--content_block > h1,
  .form--container .layout--content_block > h2,
  .form--container .layout--content_block > h3,
  .form--container .layout--content_block > h4,
  .form--container .layout--content_block > h5,
  .form--container .layout--content_block > h6,
  .form--container .layout--content_block > p,
  .form--container .layout--content_block > .layout--scroller,
  .form--container .layout--content_block > *,
  .layout--content_block.layout--content_block-big_link_container > *,
  .form--container .layout--content_block > .button--container,
  .form--container .layout--content_block > .layout--content-wide {
    grid-column: 1 / -1 !important;
  }

  .layout--viewport {
    width: 360px;
    max-width: 100vw;
  }
}

@media (max-width: 1160px) and (min-width: 801px) {
  .layout--viewport {
    width: 760px;
  }

  .layout--content_block.layout--content_block-big_link_container > * {
    grid-column: span 3;
  }
}

@media (min-width: 1401px) {
  .layout--wrapper {
    padding-left: 240px;
  }
}

/*PRINT*/
@media print {
  * {
    display: none;
  }
}

/*DEV MODE NOTIFICATION*/
.SERVER-CHILI #app::before,
.SERVER-CHILI #app::after {
  bottom: 25vh;
  color: #000000;
  content: 'DEV';
  float: right;
  font-size: 20em;
  font-weight: bold;
  position: fixed;
  right: 25vw;
  transform-origin: 65% 65%;
  transition:
    bottom 350ms ease-in-out 500ms,
    right 350ms ease-in-out 500ms,
    font-size 350ms ease-in-out 500ms;
  z-index: 10001;
}

.SERVER-CHILI.layout-page_loaded #app::before,
.SERVER-CHILI.layout-page_loaded #app::after {
  bottom: 16px;
  font-size: 2em;
  right: 16px;
}

.SERVER-CHILI #app::before {
  animation: dev-notification-color 6000ms ease-in-out infinite;
}

.SERVER-CHILI #app::after {
  animation:
    dev-notification 1250ms linear infinite,
    dev-notification-color 6000ms ease-in-out infinite;
}

@keyframes dev-notification {
  from {
    opacity: 0.5;
    transform: scale(1);
  }
  25% {
    opacity: 1;
    transform: scale(2);
  }
  75% {
    opacity: 0.25;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes dev-notification-color {
  100%,
  0% {
    color: #d62d20;
  }
  40% {
    color: #0057e7;
  }
  66% {
    color: #008744;
  }
  80%,
  90% {
    color: #ffa700;
  }
}

.body--container table td .ACTIVE {
  color: #4caf50;
}

.body--container table td .DISABLED,
.body--container table td .EXPIRED {
  color: #f44336;
}
</style>
